import { Component, OnInit, OnDestroy } from '@angular/core';
// ionic
import { ModalController } from '@ionic/angular';

// other libraries
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from 'rxjs/operators';

// extends
import { BasePage } from "../base";

// components
import { EventImprintComponent } from "../../components/event-imprint/event-imprint.component";

// services
import { UserService } from "../../services/user.service";
import { ParticipantSettingService } from "../../services/participant-setting.service";

// models
import { User } from "../../models/user";
import { Participant } from "../../models/participant";
import { ParticipantSetting } from "../../models/participant-setting";
import { ActivatedRoute, Router } from '@angular/router';
import { Event } from 'src/app/models/event';
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
    selector: 'app-setting',
    templateUrl: './setting.page.html',
    styleUrls: ['./setting.page.scss'],
})
export class SettingPage extends BasePage implements OnInit, OnDestroy {

    /**
      * unsubscribe subject
      *
      * @type {Subject<void>}
      */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * loading state
     *
     * @type {boolean}
     */
    public submit: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * participant
     *
     * @type {Participant}
     */
    public participant: Participant = new Participant;

    /**
     * settings
     *
     * @type {ParticipantSetting}
     */
    public settings: ParticipantSetting = new ParticipantSetting;

    /**
     * original settings (until the settings get saved)
     *
     * @type {ParticipantSetting}
     */
    public originalSettings: ParticipantSetting = new ParticipantSetting;

    /**
     * available languages
     *
     * @type array
     */
    public langs: any = [];

    /**
     * active user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {object}
     */
    public validationErrors: object = {};

    /**
     * Page mode - for public mail setting or in app user profile setting
     *
     * @type {boolean}
     */
    public mailView: boolean = false;

    constructor(
        private auth: AuthenticationService,
        public router: Router,
        public route: ActivatedRoute,
        public translate: TranslateService,
        public modalController: ModalController,
        public settingsService: ParticipantSettingService,
        public userService: UserService
    ) {
        super();
        this.langs = this.plt.availableLangs
        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        if (this.router.url.includes('/mail-setting')) {
            this.mailView = true;

            let participantId = this.route.snapshot.queryParams['id'];
            let token = this.route.snapshot.queryParams['t'];

            // load participant data by mail setting token
            if (!!participantId) {
                this.settingsService.getMailSetting(participantId, token).subscribe(
                    (success) => {

                        this.settings = new ParticipantSetting(success.setting);
                        this.originalSettings = new ParticipantSetting(success.setting);

                        this.event = new Event(success.event);

                        this.loading = false;
                    },
                    (error) => {
                        this.showError(error.error.message);
                        this.loading = false;
                    });
            }

        } else {

            this.userService.getCurrentUser()
                .pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe(
                    (user) => {
                        if (user.id) {
                            this.user = user;
                            this.participant = this.user.selected_participant;
                            this.event = this.participant.event;

                            user.settings.forEach(setting => {
                                if (setting.event_id === this.user.selected_participant.event_id) {
                                    this.settings = Object.assign(JSON.parse(JSON.stringify(setting)), new ParticipantSetting);
                                    this.originalSettings = JSON.parse(JSON.stringify(setting));
                                }
                            });

                            this.loading = false;
                        }
                    });
        }
    }

    /**
     * save
     *
     * @return void
     */
    public save() {
        this.submit = true;

        if (this.mailView) {
            let participantId = this.route.snapshot.queryParams['id'];
            let token = this.route.snapshot.queryParams['t'];

            // src for tracking
            let src = this.route.snapshot.queryParams['s'] || this.route.snapshot.queryParams['src'];
            let srcId = this.route.snapshot.queryParams['sId'] || this.route.snapshot.queryParams['srcId'] || null;

            // load participant data by mail setting token
            this.settingsService.setMailSetting(participantId, token, src, srcId, this.settings).subscribe(
                (success) => {

                    this.settings = new ParticipantSetting(success.settings);
                    this.originalSettings = new ParticipantSetting(success.settings);
                    this.loading = false;
                    this.submit = false;
                    this.showSuccess(success.message);
                },
                (error) => {
                    this.showError(error.error.message);

                    this.submit = false;
                });
        } else {

            this.translate.use(this.settings.locale);
            this.settingsService.update(this.settings).subscribe(
                success => {

                    // update token in case that it is refreshed
                    if (success.token) {
                        this.auth.updateToken(success.token);
                    }

                    // store password
                    if (this.user.old_password || this.user.new_password || this.user.new_password_confirmation) {
                        this.savePassword(success.message);
                    } else {
                        this.finalizeSaving(success.message);
                    }
                },
                error => {
                    this.showError(error.error.message);

                    this.submit = false;
                });
        }
    }

    /**
     * save changed password
     *
     * @return true
     */
    public savePassword(message) {
        this.validationErrors = {};
        this.validate = true;
        this.submit = true;

        if (this.user.isValid()) {
            this.userService.updatePassword(this.user).subscribe(success => {
                this.validate = false;
                this.validationErrors = {};
                // reset form
                this.user.old_password = null;
                this.user.new_password = null;
                this.user.new_password_confirmation = null;

                this.finalizeSaving(message);
            },
                error => {
                    const data = error.error;

                    if (data.fields) {
                        for (const field in data.fields) {
                            if (data.fields.hasOwnProperty(field)) {
                                this.validationErrors[field] = data.fields[field].join(' ');
                            }
                        }
                    }

                    this.showError(error.error.message);
                    this.submit = false;
                });
        } else {
            this.validationErrors = this.user.validationErrors;
            this.showError(this.translate.instant('VALIDATION_WRONG_INPUT'));
            this.submit = false;
        }


    }

    public finalizeSaving(message) {
        if (this.settings.locale) {
            this.showSuccess(message);
            this.appEvents.publish('user:current:participant');

            this.originalSettings = Object.assign(JSON.parse(JSON.stringify(this.settings)), new ParticipantSetting);
            for (let i = 0; i < this.user.settings.length; i++) {
                if (this.user.settings[i].event_id === this.user.selected_participant.event_id) {
                    this.user.settings[i] = this.originalSettings;
                }
            }

            this.userService.setCurrentUser(this.user);
        } else {
            this.showSuccess(message);
        }
        this.submit = false;
        // reload user after updating participant
    }

    public async openImprint($event) {
        $event.stopPropagation();

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: EventImprintComponent,
            cssClass: 'event-modal',
            componentProps: {
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data && data.data.action && data.data.action == 'close') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.settings = Object.assign(JSON.parse(JSON.stringify(this.originalSettings)), new ParticipantSetting);

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
